import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Grid, Stack } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { createVendorContact, VendorContactDTO } from 'wisestep-api';
import {
  ControlledTextArea,
  ControlledTextInput,
  WsButton,
  WsSelect,
  WsSelectObj,
} from 'wisestep-base';
import { object, string } from 'yup';

import { failed, success } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { vendorContactPayload } from '../helper/payload';

interface VendorContactProps {
  vendorId: string;
  onClose: () => void;
  vendorContact?: VendorContactDTO | null;
}

const validationSchema = object().shape({
  firstName: string().required('Please provide First Name'),
  emailAddress: string().required('Email is required').email('Email is invalid'),
});

const VendorContactForm = ({ vendorId, onClose, vendorContact }: VendorContactProps) => {
  const methods = useForm<VendorContactDTO>({
    resolver: yupResolver(validationSchema),
    defaultValues: vendorContact ?? vendorContactPayload,
  });
  const { register } = methods;
  const navigate = useNavigate();
  const setNotification = useNotificationStore((state) => state.setNotification);

  const statusList: WsSelectObj[] = [
    {
      label: 'Active',
      value: 'Active',
    },
    {
      label: 'Inactive',
      value: 'Inactive',
    },
  ];

  register('statusValue', {
    onChange: (e) => {
      const data = e.target.value;
      if (data) {
        methods.setValue('active', data === 'Active' ? true : false);
      }
    },
  });

  const onSubmit = async (data: VendorContactDTO) => {
    try {
      data.vendorId = vendorId;
      const response = await createVendorContact(data);
      if (response.status === 200) {
        onClose();
        setNotification(success('Vendor Contact successfully created.'));
      }
    } catch (error) {
      setNotification(failed('Error while creating vendor contact.'));
    }
  };

  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <Box borderRadius={1.5} pl={1}>
            <Grid container py={1}>
              <Grid item p={1} sm={5.75}>
                <ControlledTextInput
                  name="firstName"
                  type="text"
                  fullWidth
                  labelText="First Name"
                  variant="outlined"
                  showMandatorySymbol={true}
                />
              </Grid>
              <Grid item p={1} sm={5.75}>
                <ControlledTextInput
                  name="lastName"
                  type="text"
                  fullWidth
                  labelText="Last Name"
                  variant="outlined"
                />
              </Grid>
              <Grid item p={1} sm={5.75}>
                <ControlledTextInput
                  name="emailAddress"
                  type="email"
                  fullWidth
                  labelText="Email"
                  variant="outlined"
                  showMandatorySymbol={true}
                />
              </Grid>
              <Grid item p={1} sm={5.75}>
                <ControlledTextInput
                  name="phoneNumber"
                  type="text"
                  fullWidth
                  labelText="Phone"
                  variant="outlined"
                />
              </Grid>
              <Grid item p={1} sm={5.75}>
                <ControlledTextInput
                  name="position"
                  type="text"
                  fullWidth
                  labelText="Position"
                  variant="outlined"
                />
              </Grid>
              <Grid item p={1} sm={5.75}>
                <WsSelect
                  label="Status"
                  name="statusValue"
                  selectArray={statusList}
                  defaultValue={'Active'}
                  size="small"
                />
              </Grid>
              <Grid item p={1} sm={11.5}>
                <ControlledTextArea
                  name="physicalAddress"
                  labelText="Physical Address"
                  minRows={5}
                />
              </Grid>
            </Grid>
          </Box>
          <Stack
            p={1}
            pr={4}
            spacing={2}
            direction="row"
            display="flex"
            justifyContent={'flex-end'}
          >
            <WsButton size="large" variant="outlined" onClick={() => navigate(-1)}>
              {'Cancel'}
            </WsButton>
            <WsButton size="large" type="submit">
              {vendorContact ? 'Update' : 'Create'}
            </WsButton>
          </Stack>
        </form>
      </FormProvider>
    </>
  );
};

export default VendorContactForm;
